/* eslint-disable react/prop-types */
import T from 'prop-types';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import { useMount, useSearchParam } from 'react-use';

import Sidenav from '../pages/dashboard/components/navigation/Sidenav';
import { getUserProfile } from '../redux/actions';
import { alertConstants } from '../redux/constants';
import routes from '../routes/constants';
import { getUser, setUser } from './storage';
import { isUserAnonymous } from './user';
import {
  useVerifyTokenMutation,
  useCreateAnonymousAndValidateMutation,
} from '../redux/rtk';
import { isRouteIncluded } from './routes';

const ALLOWED_ANONYMOUS_ROUTES = [routes.mp.job(), routes.mp.map2dRoot];

const PrivateRoute = ({ component: Component, allowAnonymous, ...rest }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const token = useSearchParam('token');
  const impersonateToken = useSearchParam('impersonate');
  const searchParams = new URLSearchParams(window.location.search);
  const shareToken = rest?.computedMatch?.params?.shareToken || '';

  const userRef = useRef(getUser());

  const [verifyToken] = useVerifyTokenMutation();
  const [createAnonymousAndValidate] = useCreateAnonymousAndValidateMutation();

  const [loading, setLoading] = useState(token !== null && !userRef.current);
  const [loadingAnonymous, setLoadingAnonymous] = useState(
    !userRef.current && allowAnonymous && shareToken
  );

  const autoLogin = async () => {
    setLoading(true);
    try {
      const { data } = await verifyToken({ token }).unwrap();

      userRef.current = data;
      setUser(data, data.is_anonymous);
      dispatch(getUserProfile());
      if (token) {
        searchParams.delete('token');
      }
      history.replace({
        search: searchParams.toString(),
      });
    } catch (error) {
      dispatch({
        type: alertConstants.ALERT_ERROR,
        payload: {
          message: error.data?.message || error.data || 'Auto login failed',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const createAnonymousUserAndValidateHandler = async () => {
    setLoadingAnonymous(true);
    try {
      const { user } = await createAnonymousAndValidate({
        shareToken,
        allowedAnonymousRoutes: ALLOWED_ANONYMOUS_ROUTES,
      }).unwrap();

      userRef.current = user;
      setUser(user, user.is_anonymous);
      dispatch(getUserProfile());
    } catch (error) {
      dispatch({
        type: alertConstants.ALERT_ERROR,
        payload: {
          message:
            error.data?.message ||
            error.data ||
            'Anonymous user creation failed',
        },
      });
    } finally {
      setLoadingAnonymous(false);
    }
  };

  const impersonate = () => {
    setUser(
      {
        token: impersonateToken,
      },
      true
    );
    dispatch(getUserProfile());
    history.push(routes.home);
  };

  useMount(() => {
    if (token && !userRef.current) {
      autoLogin();
    } else if (impersonateToken) {
      impersonate();
    } else if (!userRef.current && allowAnonymous && shareToken) {
      createAnonymousUserAndValidateHandler();
    }
  });

  const getQueryString = (search) => {
    const queryParams = new URLSearchParams(search);

    if (queryParams.size === 0) {
      return '';
    }

    if (queryParams.has('token')) {
      queryParams.delete('token');
    }

    return `?${queryParams.toString()}`;
  };

  const is2DMap = isRouteIncluded([
    routes.order.root,
    routes.mp.map2dRoot,
    routes.mp.root,
  ]);
  const location = useLocation();
  const hideSidenav = is2DMap(location.pathname);

  return (
    <Route
      {...rest}
      render={(props) =>
        loading || loadingAnonymous ? (
          <PulseLoader />
        ) : (userRef.current && !isUserAnonymous(userRef.current)) ||
          (allowAnonymous && isUserAnonymous(userRef.current)) ? (
          <>
            {!hideSidenav && <Sidenav />}
            <Component {...props} />
          </>
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              search: `?redirectUrl=${encodeURIComponent(
                `${props.location.pathname}${getQueryString(
                  props.location.search
                )}`
              )}`,
              state: {
                from: props.location,
              },
            }}
          />
        )
      }
    />
  );
};

PrivateRoute.propTypes = {
  component: T.elementType.isRequired,
  allowAnonymous: T.bool,
};

export default PrivateRoute;
